import { useState } from 'react';
import { Modal, Box, Button, Checkbox, FormControlLabel, IconButton, Fade, TextField } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarEmpty } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import '../translations.js';
import './AppRatingModal.css';

const AppRatingModal = ({ open, onClose, userId }) => {
  const [stars, setStars] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState('rating');
  const [feedback, setFeedback] = useState('');
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const MODAL_NAME = 'app_rating_modal';
  const WIX_REVIEW_URL = `https://www.wix.com/app-market/add-review/${process.env.REACT_APP_WIX_APP_ID}`;

  const resetState = () => {
    setStars(0);
    setHoveredStar(0);
    setDoNotShowAgain(false);
    setStep('rating');
    setFeedback('');
  };

  const dismissModal = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}user_modal_preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, modal_name: MODAL_NAME })
      });
    } catch (error) {
      console.error('Error saving modal preference:', error);
    }
  };

  const saveRating = async (feedbackText) => {
    const payload = { user_id: userId, stars };
    const trimmed = (feedbackText || '').trim();
    if (trimmed) payload.feedback = trimmed;
    await fetch(`${process.env.REACT_APP_API_BASE_URL}app_ratings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  };

  const handleClose = async () => {
    if (submitting) return;
    if (step === 'feedback') {
      setSubmitting(true);
      try {
        await saveRating(feedback);
        await dismissModal();
      } catch (error) {
        console.error('Error submitting rating:', error);
      } finally {
        setSubmitting(false);
      }
    } else if (doNotShowAgain) {
      await dismissModal();
    }
    resetState();
    onClose();
  };

  const handleDoNotShowAgainChange = async (checked) => {
    setDoNotShowAgain(checked);
    if (checked) {
      await dismissModal();
      resetState();
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (stars === 0 || submitting) return;

    if (stars < 5 && step === 'rating') {
      setStep('feedback');
      return;
    }

    setSubmitting(true);
    try {
      await saveRating(step === 'feedback' ? feedback : '');
      await dismissModal();

      if (stars === 5) {
        window.open(WIX_REVIEW_URL, '_blank', 'noopener,noreferrer');
      }

      resetState();
      onClose();
    } catch (error) {
      console.error('Error submitting rating:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const displayStars = hoveredStar || stars;

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open} timeout={400}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            borderRadius: '10px',
            boxShadow: 24,
            p: isMobile ? 2.5 : 4,
            width: isMobile ? '85%' : '90%',
            maxWidth: 460,
            outline: 'none',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
            aria-label="close rating modal"
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>

          {step === 'rating' ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <h2 className="app-rating-title">
                  {t('rate_your_experience', 'How do you like Ultimo Bots?')}
                </h2>
                <p className="app-rating-subtitle">
                  {t('rate_subtitle', 'Your feedback helps us improve')}
                </p>
              </Box>

              <Box className="app-rating-stars-container">
                {[1, 2, 3, 4, 5].map((value) => (
                  <span
                    key={value}
                    className={`app-rating-star ${value <= displayStars ? 'filled' : ''}`}
                    onClick={() => setStars(value)}
                    onMouseEnter={() => setHoveredStar(value)}
                    onMouseLeave={() => setHoveredStar(0)}
                  >
                    <FontAwesomeIcon icon={value <= displayStars ? faStar : faStarEmpty} />
                  </span>
                ))}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={doNotShowAgain}
                    onChange={(e) => handleDoNotShowAgainChange(e.target.checked)}
                    sx={{
                      color: '#5616ea',
                      '&.Mui-checked': { color: '#5616ea' },
                    }}
                  />
                }
                label={t('do_not_show_again', 'Do not show this again')}
                sx={{ mt: 1, mb: 1, display: 'block' }}
              />
            </>
          ) : (
            <Box sx={{ mb: 2 }}>
              <h2 className="app-rating-title" style={{ textAlign: 'center' }}>
                {t('rate_improve_title', 'What can we improve?')}
              </h2>
              <p className="app-rating-subtitle" style={{ textAlign: 'center', marginBottom: 16 }}>
                {t('rate_improve_subtitle', 'Tell us what would have made this a 5-star experience.')}
              </p>
              <TextField
                autoFocus
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={t('rate_improve_placeholder', 'Your feedback...')}
                inputProps={{ maxLength: 5000 }}
                disabled={submitting}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#5616ea',
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={submitting}
              sx={{
                color: '#5616ea',
                borderColor: '#5616ea',
                '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
              }}
            >
              {step === 'feedback' ? t('skip', 'Skip') : t('cancel')}
            </Button>

            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={stars === 0 || submitting}
              sx={{
                backgroundColor: '#5616ea',
                '&:hover': { backgroundColor: '#4a14d6' },
              }}
            >
              {submitting ? t('submitting', 'Submitting...') : t('submit', 'Submit')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default AppRatingModal;
